import React, { useContext, useEffect, useState } from 'react';
import {
  FieldArrayWithId,
  FieldErrors,
  FormProvider,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { StyledCard, CardTitle, StyledFormGroup } from '../cardStyles';
import { InfoRow, SectionLabel } from '../style';
import { formaPagamentoLabel } from '../constants';
import Payment from '../../../OrderForm/Sections/Payment';
import { buildInstallments, parseConditionToOffsets } from '../../../OrderForm/utils';
import { useNotaInstallments } from '../useNotaInstallments';
import {
  NotaPagamentoFormValues,
  notaPagamentoSchema,
} from './notaPagamento.schema';
import {
  HeaderRow,
  EditToggleButton,
  RowsGrid,
  ParcelaRowGrid,
  CellInput,
  TotalsBanner,
  AdjustLink,
  ConditionBadge,
  SaveCancelBar,
  SmallOutlineButton,
  SmallSolidButton,
  EditedNotice,
} from './PagamentoSection.style';

interface Props {
  invoice: any;
  invoiceId: number;
  readOnly?: boolean;
  onSaved: () => void;
}

type Condicao = 'A_VISTA' | 'A_PRAZO' | 'OUTROS';

const formatBrl = (val: number) =>
  val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatBrlNumber = (n: number) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseBrlNumber = (text: string): number => {
  const n = Number(text.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const toDateInputValue = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Condição sai do resultado da edição, nunca digitada: 1 parcela vencendo hoje (ou
// antes) é à vista; mais de uma parcela, ou vencimento futuro, é a prazo.
function derivarCondicao(parcelas: { vencimento: string }[]): Condicao {
  if (parcelas.length === 0) return 'OUTROS';
  if (parcelas.length > 1) return 'A_PRAZO';
  const [unica] = parcelas;
  if (!unica.vencimento) return 'OUTROS';
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const vencimento = new Date(`${unica.vencimento}T00:00:00`);
  return vencimento.getTime() <= hoje.getTime() ? 'A_VISTA' : 'A_PRAZO';
}

const CONDICAO_LABEL: Record<Condicao, string> = {
  A_VISTA: 'À vista',
  A_PRAZO: 'A prazo',
  OUTROS: 'Outros',
};

// Rateia proporcionalmente ao novo total, última linha absorve a sobra — ajuda opcional ao
// usuário quando o total da nota diverge da soma das parcelas.
function distribuirProporcional<T extends { valor: number }>(linhas: T[], total: number): T[] {
  if (linhas.length === 0) return linhas;
  const soma = linhas.reduce((s, l) => s + l.valor, 0);
  const fator = soma > 0 ? total / soma : 0;
  let alocado = 0;
  return linhas.map((linha, idx) => {
    const valor =
      idx === linhas.length - 1
        ? round2(total - alocado)
        : round2(soma > 0 ? linha.valor * fator : total / linhas.length);
    alocado += valor;
    return { ...linha, valor };
  });
}

const ValorCell: React.FC<{ valor: number; onChange: (n: number) => void; disabled?: boolean }> = ({
  valor,
  onChange,
  disabled,
}) => {
  const [texto, setTexto] = useState(() => formatBrlNumber(valor));
  const [focado, setFocado] = useState(false);

  useEffect(() => {
    if (!focado) setTexto(formatBrlNumber(valor));
  }, [valor, focado]);

  return (
    <CellInput
      type="text"
      inputMode="decimal"
      value={texto}
      disabled={disabled}
      onFocus={() => setFocado(true)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => {
        setFocado(false);
        onChange(parseBrlNumber(texto));
      }}
    />
  );
};

/** Descrição livre da forma "Outros" — mesma exigência do formulário de pedido. */
const MethodDescriptionField: React.FC = () => {
  const { register, setValue, watch, formState: { errors } } = useFormContext<NotaPagamentoFormValues>();
  const paymentMethodName = watch('paymentMethodName');
  const isOutros = paymentMethodName === 'Outros';

  useEffect(() => {
    if (!isOutros) setValue('descricao', undefined, { shouldDirty: true });
  }, [isOutros, setValue]);

  if (!isOutros) return null;

  return (
    <StyledFormGroup>
      <label htmlFor="descricao">Descrição da Forma</label>
      <input id="descricao" {...register('descricao')} placeholder="Descreva a forma de pagamento" />
      {errors.descricao && (
        <span style={{ color: '#e53e3e', fontSize: '11px' }}>{errors.descricao.message}</span>
      )}
    </StyledFormGroup>
  );
};

interface NotaInstallmentsTableProps {
  fields: FieldArrayWithId<NotaPagamentoFormValues, 'installments', 'id'>[];
  update: UseFieldArrayUpdate<NotaPagamentoFormValues, 'installments'>;
  replace: UseFieldArrayReplace<NotaPagamentoFormValues, 'installments'>;
  sumInstallments: number;
  hasMismatch: boolean;
  total: number;
  condicaoAtual: Condicao;
}

/** Parcelas nascem da condição (P2) — aqui só o ajuste pontual de valor/vencimento (P3). */
const NotaInstallmentsTable: React.FC<NotaInstallmentsTableProps> = ({
  fields,
  update,
  replace,
  sumInstallments,
  hasMismatch,
  total,
  condicaoAtual,
}) => (
  <div style={{ marginTop: '20px' }}>
    <HeaderRow>
      <SectionLabel>Parcelas</SectionLabel>
      <ConditionBadge>{CONDICAO_LABEL[condicaoAtual]}</ConditionBadge>
    </HeaderRow>

    {fields.length === 0 ? (
      <div style={{ fontSize: '12px', color: '#999' }}>
        Escolha uma condição de pagamento para gerar as parcelas.
      </div>
    ) : (
      <RowsGrid style={{ marginTop: '8px' }}>
        {fields.map((field, index) => {
          const { id, ...rest } = field;
          return (
            <ParcelaRowGrid key={field.id}>
              <span style={{ color: '#666', fontWeight: 600 }}>{index + 1}ª</span>
              <ValorCell valor={field.valor} onChange={(valor) => update(index, { ...rest, valor })} />
              <CellInput
                type="date"
                value={field.vencimento}
                onChange={(e) => update(index, { ...rest, vencimento: e.target.value })}
              />
            </ParcelaRowGrid>
          );
        })}
      </RowsGrid>
    )}

    {fields.length > 0 && (
      <TotalsBanner $ok={!hasMismatch} style={{ marginTop: '10px' }}>
        <span>
          Parcelas somam {formatBrl(sumInstallments)} · nota {formatBrl(total)}
        </span>
        {hasMismatch && (
          <AdjustLink
            type="button"
            onClick={() => replace(distribuirProporcional(fields.map(({ id, ...rest }) => rest), total))}
          >
            Ajustar automaticamente
          </AdjustLink>
        )}
      </TotalsBanner>
    )}
  </div>
);

const LegacyMultiplasFormasNotice: React.FC = () => (
  <EditedNotice>
    Nota gravada com mais de uma forma de pagamento pelo formato anterior — exibindo a primeira.
  </EditedNotice>
);

const PagamentoSection: React.FC<Props> = ({ invoice, invoiceId, readOnly, onSaved }) => {
  const { setFeedback } = useContext(FeedbackContext);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const snapshotFormas = invoice?.InvoicePayment ?? [];
  const snapshotParcelas = invoice?.InvoiceInstallment ?? [];
  const orderFormas = invoice?.Order?.OrderPaymentMethod ?? [];
  const orderParcelas = invoice?.Order?.OrderInstallment ?? [];
  const totalNota = Number(invoice?.valorTotalNota) || 0;
  const pagamentoEditado = !!invoice?.pagamentoEditado;
  const formaMultiplaLegada = snapshotFormas.length > 1;

  const condicaoPersistida = invoice?.condicaoPagamento || invoice?.Order?.PaymentCondition?.name;
  const isAVistaPersistido =
    invoice?.condicaoPagamento === 'A_VISTA' || /vista/i.test(condicaoPersistida || '');
  const formasExibidas = snapshotFormas.length > 0 ? snapshotFormas.slice(0, 1) : null;
  // à vista não gera duplicata, então não existe snapshot nem parcela no pedido para herdar —
  // a condição da nota já determina a parcela: uma só, no total, vencendo na emissão
  const parcelaUnicaAVista = isAVistaPersistido ? buildInstallments(totalNota, [0]) : [];
  const parcelasExibidas =
    snapshotParcelas.length > 0
      ? snapshotParcelas
      : isAVistaPersistido
      ? parcelaUnicaAVista
      : orderParcelas;
  const condicaoLabel = isAVistaPersistido
    ? 'À vista'
    : condicaoPersistida === 'A_PRAZO'
    ? 'A prazo'
    : condicaoPersistida || '—';

  const buildDefaultValues = (): NotaPagamentoFormValues => {
    if (snapshotFormas.length > 0) {
      const forma = snapshotFormas[0];
      return {
        paymentMethodId: forma.paymentMethodId,
        paymentMethodName: forma.PaymentMethod?.name ?? '',
        paymentConditionId: undefined,
        paymentConditionName: undefined,
        descricao: forma.descricao ?? undefined,
        installments: snapshotParcelas.map((p: any) => ({
          numero: p.numeroParcela,
          valor: Number(p.valorParcela ?? 0),
          vencimento: toDateInputValue(p.vencimento),
        })),
      };
    }

    if (orderFormas.length === 1) {
      const forma = orderFormas[0];
      const condition = invoice?.Order?.PaymentCondition;
      const offsets = parseConditionToOffsets(condition?.name);
      return {
        paymentMethodId: forma.paymentMethodId ?? forma.PaymentMethod?.id,
        paymentMethodName: forma.PaymentMethod?.name ?? '',
        paymentConditionId: invoice?.Order?.paymentConditionId ?? undefined,
        paymentConditionName: condition?.name ?? undefined,
        descricao: forma.description ?? undefined,
        installments: buildInstallments(totalNota, offsets).map(({ numero, valor, vencimento }) => ({
          numero,
          valor,
          vencimento,
        })),
      };
    }

    if (isAVistaPersistido) {
      return {
        paymentMethodId: undefined,
        paymentMethodName: undefined,
        paymentConditionId: undefined,
        paymentConditionName: undefined,
        descricao: undefined,
        installments: parcelaUnicaAVista.map(({ numero, valor, vencimento }) => ({
          numero,
          valor,
          vencimento,
        })),
      };
    }

    return {
      paymentMethodId: undefined,
      paymentMethodName: undefined,
      paymentConditionId: undefined,
      paymentConditionName: undefined,
      descricao: undefined,
      installments: [],
    };
  };

  const form = useForm<NotaPagamentoFormValues>({
    resolver: zodResolver(notaPagamentoSchema),
    defaultValues: buildDefaultValues(),
  });

  const { fields, update, replace, sumInstallments, hasMismatch, marcarHidratando } = useNotaInstallments({
    control: form.control,
    watch: form.watch,
    total: totalNota,
  });

  const condicaoAtual = derivarCondicao(form.watch('installments') || []);

  const beginEdit = () => {
    const defaults = buildDefaultValues();
    marcarHidratando(defaults.paymentConditionId);
    form.reset(defaults);
    setEditing(true);
  };

  const handleInvalido = (errors: FieldErrors<NotaPagamentoFormValues>) => {
    const message =
      errors.paymentMethodId?.message ||
      errors.installments?.message ||
      errors.descricao?.message ||
      'Verifique os campos de pagamento.';
    setFeedback({ message, isOpen: true, statusOk: false });
  };

  const handleSalvar = async (data: NotaPagamentoFormValues) => {
    setSaving(true);
    try {
      await ApiService.fiscal.updateInvoice(invoiceId, {
        condicaoPagamento: derivarCondicao(data.installments),
        payments: [
          {
            paymentMethodId: Number(data.paymentMethodId),
            valor: totalNota,
            descricao: data.descricao || undefined,
          },
        ],
        installments: data.installments.map((p, idx) => ({
          numeroParcela: idx + 1,
          valorParcela: p.valor,
          vencimento: p.vencimento,
        })),
      });
      setEditing(false);
      setFeedback({ message: 'Pagamento da nota atualizado.', isOpen: true, statusOk: true });
      onSaved();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({
        message: Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao salvar o pagamento da nota.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <StyledCard>
        {formaMultiplaLegada && <LegacyMultiplasFormasNotice />}

        <FormProvider {...form}>
          <Payment methodDescriptionSlot={<MethodDescriptionField />}>
            <NotaInstallmentsTable
              fields={fields}
              update={update}
              replace={replace}
              sumInstallments={sumInstallments}
              hasMismatch={hasMismatch}
              total={totalNota}
              condicaoAtual={condicaoAtual}
            />
          </Payment>
        </FormProvider>

        <SaveCancelBar>
          <SmallOutlineButton type="button" onClick={() => setEditing(false)} disabled={saving}>
            Cancelar
          </SmallOutlineButton>
          <SmallSolidButton
            type="button"
            onClick={form.handleSubmit(handleSalvar, handleInvalido)}
            disabled={saving}
          >
            {saving ? 'Salvando…' : 'Salvar pagamento'}
          </SmallSolidButton>
        </SaveCancelBar>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <HeaderRow>
        <CardTitle style={{ paddingBottom: 0 }}>Pagamento</CardTitle>
        {!readOnly && <EditToggleButton onClick={beginEdit}>Editar pagamento</EditToggleButton>}
      </HeaderRow>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '13px' }}>
        {pagamentoEditado && (
          <EditedNotice>
            Pagamento personalizado nesta nota — diferente do pedido de origem.
          </EditedNotice>
        )}
        {formaMultiplaLegada && <LegacyMultiplasFormasNotice />}

        <InfoRow>
          <span style={{ color: '#666' }}>Condição</span>
          <strong>{condicaoLabel}</strong>
        </InfoRow>

        <div>
          <SectionLabel>Formas</SectionLabel>
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {formasExibidas ? (
              formasExibidas.map((pg: any) => (
                <InfoRow key={pg.id}>
                  <span>{formaPagamentoLabel(pg.PaymentMethod?.name, pg.paymentMethodId)}</span>
                  <strong>{formatBrl(Number(pg.valor || 0))}</strong>
                </InfoRow>
              ))
            ) : orderFormas.length > 0 ? (
              orderFormas.map((opm: any, i: number) => (
                <InfoRow key={i}>
                  <span>{formaPagamentoLabel(opm.PaymentMethod?.name, opm.paymentMethodId)}</span>
                  <span style={{ fontSize: '11px', color: '#999' }}>valor definido na emissão</span>
                </InfoRow>
              ))
            ) : (
              <div style={{ fontSize: '12px', color: '#999' }}>Sem formas registradas</div>
            )}
          </div>
        </div>

        {parcelasExibidas.length > 0 && (
          <div>
            <SectionLabel>Parcelas</SectionLabel>
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {parcelasExibidas.map((parc: any, i: number) => (
                <InfoRow key={parc.id ?? i}>
                  <span>
                    {(parc.numeroParcela ?? parc.numero ?? i + 1)}ª —{' '}
                    {parc.vencimento ? new Date(parc.vencimento).toLocaleDateString('pt-BR') : '—'}
                  </span>
                  <span>{formatBrl(Number(parc.valorParcela ?? parc.valor ?? 0))}</span>
                </InfoRow>
              ))}
            </div>
          </div>
        )}
      </div>
    </StyledCard>
  );
};

export default PagamentoSection;
